import { isPlatform } from '@ionic/react';
import { useCallback, useMemo } from 'react';

import { getDatosExtra as axiosGetDatosExtra } from '../../axios/Otros';
import { DatosExtra, DatosExtraGrouped } from '../../types/Otros';
import { useFetchEntity } from '../Utils/useEntity';
import { AlertOptions } from '../UI/useAlert';

export const useDatosExtra = () => {
  const { data, error, isValidating, mutate } = useFetchEntity<DatosExtra[]>(
    'vacuna',
    axiosGetDatosExtra,
  );

  const datosExtra = useMemo(() => {
    const result = data?.reduce<DatosExtraGrouped>(
      (acc, d) => {
        switch (d.nombre) {
          case 'texto':
          case 'descripcion':
          case 'link':
            acc.vacuna[d.nombre] = d.valor;
            break;

          case 'planes':
            acc.planes.texto = d.valor;
            break;

          case 'youtube':
            acc.youtube.link = d.valor;
            break;

          case 'celTigre':
            acc.telefonia.celTigre = d.valor;
            break;

          default:
            break;
        }

        return acc;
      },
      {
        vacuna: {},
        planes: {},
        youtube: {},
        telefonia: {},
      },
    );

    // Telefonía reutiliza el link
    if (result?.vacuna?.link) {
      result.telefonia.link = result.vacuna.link;
    }

    return result;
  }, [data]);

  return { datosExtra, isValidating, error, mutate };
};

/* ──────────────────────────────────────────────
 * Consulta telefónica
 * ────────────────────────────────────────────── */

const DAWER_PHONE_CONSULTATION_START_MINUTES = 8 * 60;
const DAWER_PHONE_CONSULTATION_END_MINUTES = 21 * 60;

export const DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT: AlertOptions = {
  header: 'Telemedicina',
  message: 'La atención telefónica está disponible de 8 a 21 hs.',
  buttons: [{ text: 'Entendido' }],
};

export const isDawerPhoneConsultationOpen = (date = new Date()): boolean => {
  const currentMinutes = date.getHours() * 60 + date.getMinutes();
  return (
    currentMinutes >= DAWER_PHONE_CONSULTATION_START_MINUTES &&
    currentMinutes < DAWER_PHONE_CONSULTATION_END_MINUTES
  );
};

type UsePhoneConsultationParams = {
  phoneLink: string | undefined;
  showAlert: (options: AlertOptions) => void;
  canOpen?: () => boolean;
  unavailableAlert?: AlertOptions;
};

export const usePhoneConsultation = ({
  phoneLink,
  showAlert,
  canOpen,
  unavailableAlert,
}: UsePhoneConsultationParams) => {
  // Número limpio (solo dígitos) para llamadas en móvil
  const rawNumber = phoneLink?.replace('tel:', '').replace(/\D/g, '').trim();
  // Número formateado para mostrar en desktop: 08001231234 → 0800-123-1234
  const phoneNumber = rawNumber?.replace(/(\d{4})(\d{3})(\d{4})/, '$1-$2-$3');

  const openPhoneConsultation = useCallback(() => {
    if (!rawNumber || !phoneNumber) return;

    if (canOpen && !canOpen()) {
      showAlert(unavailableAlert ?? DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT);
      return;
    }

    if (isPlatform('desktop')) {
      showAlert({
        header: 'Consulta telefónica',
        message: `Tramitá tus consultas a este número: ${phoneNumber}`,
        buttons: [{ text: 'Entendido' }],
      });
    } else {
      window.location.href = `tel:${rawNumber}`;
    }
  }, [rawNumber, phoneNumber, canOpen, unavailableAlert, showAlert]);

  return { openPhoneConsultation, phoneNumber };
};
